'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

// BL-966: every navigation to a new route starts at the top, back/forward
// included. Browsers restore the previous scroll position on back/forward, so
// going back to a page left at its footer lands at the footer again; Richard
// reproduced it that way and the business asked for the top instead.
//
// Two pieces:
// - RouteChangeScrollToTop, in the root layout, turns off the browser's scroll
//   restoration and scrolls to the top whenever the pathname changes, on every
//   route.
// - ScrollToTopOnNavigation, rendered after the content of a CMS page, scrolls
//   again once that content has mounted. Steve's video shows the page arriving
//   at the top while its loading skeleton is up and ending at the footer once
//   the content streams in, which the layout's scroll (fired at the skeleton)
//   would not cover.
//
// Links to a #hash target are left alone so the browser can jump to it, and so
// is a query-only change (same pathname), such as a variant or a filter.
let pendingNavigation = false;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'click',
    (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target?.closest?.('a[href]');
      if (!link || link.target === '_blank') return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash) return;

      pendingNavigation = true;
    },
    true,
  );

  window.addEventListener('popstate', () => {
    pendingNavigation = !window.location.hash;
  });
}

function scrollToTop() {
  if (window.scrollY > 0) {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }
}

export function RouteChangeScrollToTop() {
  const pathname = usePathname();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    if (window.location.hash) return;

    // One frame later, so it applies after the browser has laid out the new route.
    const frame = requestAnimationFrame(scrollToTop);
    return () => cancelAnimationFrame(frame);
  }, [pathname]);

  return null;
}

export default function ScrollToTopOnNavigation() {
  useEffect(() => {
    if (!pendingNavigation) return;

    // One frame later, so it applies after the browser has laid out the new
    // content rather than racing the commit. The flag is cleared inside the
    // frame, not before it: if the effect is torn down and re-run first (Strict
    // Mode does exactly that in dev), the cancelled frame must not have used it up.
    const frame = requestAnimationFrame(() => {
      pendingNavigation = false;
      scrollToTop();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return null;
}
